import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Key, Lock, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  
  const [step, setStep] = useState(1); // 1: Email, 2: Token + New Password, 3: Success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Build login URL with redirect parameter
  const loginUrl = redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login';

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to request password reset');
      }

      if (data.token) {
        setGeneratedToken(data.token);
        toast.success('Reset token generated!');
      } else {
        toast.info(data.message);
      }
      setStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          token: token || generatedToken,
          new_password: newPassword 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      toast.success('Password reset successfully!');
      setStep(3);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4 flex items-center">
        <div className="max-w-md mx-auto w-full">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              {/* Laikos Logo */}
              <div className="flex justify-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laikos-school/artifacts/tkonto33_IMG_1836.jpeg"
                  alt="Laikos Lay Schools Logo"
                  className="w-20 h-20 object-contain rounded-full"
                  style={{ background: 'transparent' }}
                />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">
                {step === 1 && 'Forgot Password'}
                {step === 2 && 'Reset Password'}
                {step === 3 && 'Password Reset'}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Enter your email to receive a reset token'}
                {step === 2 && 'Enter the token and your new password'}
                {step === 3 && 'Your password has been reset successfully'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 1 && (
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail size={16} /> Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      required
                      data-testid="forgot-password-email"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]"
                    disabled={loading}
                    data-testid="request-reset-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Requesting...</>
                    ) : (
                      'Request Reset Token'
                    )}
                  </Button>
                </form>
              )}

              {step === 2 && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {generatedToken && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800 font-medium mb-1">Your Reset Token:</p>
                      <p className="text-2xl font-bold text-green-600 tracking-wider text-center">{generatedToken}</p>
                      <p className="text-xs text-green-600 mt-2">This token expires in 1 hour. Keep it safe!</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="token" className="flex items-center gap-2">
                      <Key size={16} /> Reset Token
                    </Label>
                    <Input
                      id="token"
                      type="text"
                      value={token || generatedToken}
                      onChange={(e) => setToken(e.target.value.toUpperCase())}
                      placeholder="Enter reset token"
                      required
                      className="uppercase tracking-wider"
                      data-testid="reset-token-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="flex items-center gap-2">
                      <Lock size={16} /> New Password
                    </Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                      <Lock size={16} /> Confirm Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]"
                    disabled={loading}
                    data-testid="reset-password-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setStep(1)}
                  >
                    <ArrowLeft size={16} className="mr-2" /> Back to Email
                  </Button>
                </form>
              )}

              {step === 3 && (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <CheckCircle size={64} className="text-green-500" />
                  </div>
                  <p className="text-gray-600">
                    Your password has been reset successfully. You can now login with your new password.
                  </p>
                  {redirectUrl && (
                    <p className="text-sm text-[#5f7c8a] bg-blue-50 p-2 rounded">
                      After login, you will be redirected back to the school page.
                    </p>
                  )}
                  <Link to={loginUrl}>
                    <Button className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]" data-testid="go-to-login-btn">
                      Go to Login
                    </Button>
                  </Link>
                </div>
              )}

              {step !== 3 && (
                <p className="text-center mt-6 text-sm text-gray-600">
                  Remember your password?{' '}
                  <Link to={loginUrl} className="text-[#5f7c8a] hover:underline font-medium">
                    Login here
                  </Link>
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
